import Vue from 'vue';
import { runtime } from './bridge';
import { computeQueuePanelGeometry, type QueueViewport } from './queue-panel-geometry';

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

/**
 * Keep the queue popup inside the *visual* viewport.  `innerHeight` is the
 * layout viewport and can be taller than the visible window when a browser
 * zoom/OSK/embedded player changes the visual viewport, which was the source
 * of the top-clipped queue card reported in SM-BUG-042.
 */
export function queueViewport(): QueueViewport {
  const viewport = typeof window !== 'undefined' ? window.visualViewport : undefined;
  return {
    left: Math.max(0, Number(viewport?.offsetLeft) || 0),
    top: Math.max(0, Number(viewport?.offsetTop) || 0),
    width: Math.max(1, Number(viewport?.width) || (typeof innerWidth === 'number' ? innerWidth : 1)),
    height: Math.max(1, Number(viewport?.height) || (typeof innerHeight === 'number' ? innerHeight : 1)),
  };
}


export const queuePanel = Vue.observable({ x: 16, top: 16, maxHeight: 560, width: 360 });
let returnFocus: HTMLElement | null = null;
let anchorBox: { x: number; y: number; width: number; height: number } | null = null;

function reservedBottomSpace(viewport: QueueViewport): number {
  const dock = document.querySelector<HTMLElement>('#bottom-bar');
  if (!dock) return 0;
  const rect = dock.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0 || rect.top >= viewport.top + viewport.height || rect.bottom <= viewport.top) return 0;
  // Keep a small gap above the visible control bar. Do not reserve the whole
  // layout viewport when the bar is hidden or outside the visual viewport.
  return Math.max(0, viewport.top + viewport.height - rect.top + 12);
}

export function repositionQueuePanel() {
  const viewport = queueViewport();
  const geometry = computeQueuePanelGeometry(anchorBox, viewport, { reservedBottom: reservedBottomSpace(viewport) });
  queuePanel.x = geometry.x;
  queuePanel.top = geometry.top;
  queuePanel.maxHeight = geometry.maxHeight;
  queuePanel.width = geometry.width;
  return geometry;
}

export function closeQueue(restoreFocus = true) {
  runtime.ui.queueOpen = false;
  anchorBox = null;
  if (restoreFocus) Vue.nextTick(() => {
    if (returnFocus?.isConnected && returnFocus.getClientRects().length) returnFocus.focus();
  });
}

export function toggleQueue(anchor?: HTMLElement, embedded = false) {
  if (runtime.ui.queueOpen) { closeQueue(); return; }
  let box: DOMRect | undefined;
  // Safari does not necessarily focus a button on touch/click. The explicit
  // anchor is therefore the return target, even if activeElement is body or a
  // previously focused control when the popup opens.
  returnFocus = anchor instanceof HTMLElement ? anchor : document.activeElement instanceof HTMLElement ? document.activeElement : null;
  if (embedded) {
    const frame = document.querySelector<HTMLIFrameElement>('iframe[title="AMLL 官方播放页面"]');
    const trigger = frame?.contentDocument?.querySelector<HTMLElement>('[data-salt-controls] [aria-label="播放队列"],button[aria-label="OpenPlaylist"],button[aria-label="播放队列"]');
    if (frame && trigger) {
      const outer = frame.getBoundingClientRect(), inner = trigger.getBoundingClientRect();
      box = new DOMRect(outer.x + inner.x, outer.y + inner.y, inner.width, inner.height);
      returnFocus = trigger;
    }
  } else if (anchor) box = anchor.getBoundingClientRect();
  anchorBox = box ? { x: box.x, y: box.y, width: box.width, height: box.height } : null;
  repositionQueuePanel();
  runtime.ui.audioSettingsOpen = false;
  runtime.ui.queueOpen = true;
}
